import { ensureStorefrontEndpointUrl } from '@/shared/utils/storefront-endpoint';

import type { ShopifyResource } from './shopify-resource-path';

const RESOURCE_QUERIES: Record<ShopifyResource['type'], string> = {
  product:
    'query($handle: String!) { productByHandle(handle: $handle) { id } }',
  collection:
    'query($handle: String!) { collectionByHandle(handle: $handle) { id } }',
  page: 'query($handle: String!) { page(handle: $handle) { id } }'
};

const RESOURCE_FIELD: Record<ShopifyResource['type'], string> = {
  product: 'productByHandle',
  collection: 'collectionByHandle',
  page: 'page'
};

type StorefrontExistenceResponse = {
  data?: Record<string, { id: string } | null>;
};

export type ResourceExistenceStatus = 'exists' | 'missing' | 'unknown';

// Shopify's Storefront API is CORS-open by design (unlike the plain .json
// resource endpoints), so this can run cross-origin, straight from the
// browser, against the target store's own public storefront token - no
// backend proxy needed.
//
// "missing" and "unknown" are kept apart because callers disagree on what a
// failed check should mean: the nudge treats both as "don't preserve the
// path", while the footer switcher fails open and preserves it, so that an
// ad blocker doesn't silently downgrade every country link to a homepage.
export const resolveResourceOnStore = async (
  storefrontEndpoint: string,
  publicAccessToken: string,
  resource: ShopifyResource
): Promise<ResourceExistenceStatus> => {
  try {
    const endpoint = ensureStorefrontEndpointUrl(storefrontEndpoint);

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': publicAccessToken
      },
      body: JSON.stringify({
        query: RESOURCE_QUERIES[resource.type],
        variables: { handle: resource.handle }
      })
    });

    if (!response.ok) {
      return 'unknown';
    }

    const { data }: StorefrontExistenceResponse = await response.json();
    if (!data) {
      return 'unknown';
    }

    const field = data[RESOURCE_FIELD[resource.type]];
    if (field) {
      return 'exists';
    }
    if (field === null) {
      return 'missing';
    }
    return 'unknown';
  } catch {
    return 'unknown';
  }
};

export const checkResourceExistsOnStore = async (
  storefrontEndpoint: string,
  publicAccessToken: string,
  resource: ShopifyResource
): Promise<boolean> =>
  (await resolveResourceOnStore(
    storefrontEndpoint,
    publicAccessToken,
    resource
  )) === 'exists';
